import {
  GoogleMap,
  LoadScriptNext,
  OverlayView,
  OverlayViewF,
} from '@react-google-maps/api';
import { memo, useCallback } from 'react';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import Spinner from '@/components/shared/ui/loading/Spinner';

import theme from '@/styles/theme';

import CustomMarker, { LogoProps } from './CustomMarker';
import { darkModeStyle } from './styles';

/**
 * width/height 는 호출부가 'auto' / '100%' / '320px' 처럼 자유롭게 주는 문자열이라
 * 클래스로 만들 수 없어 style 로 넘긴다. 원본과 동일하게 falsy 면 선언하지 않는다.
 * google-map-container 클래스는 구글맵 내부 DOM(.gm-style iframe + div)을 겨냥한
 * 자식 셀렉터용이며 실제 규칙은 globals.css 에 있다.
 */
const GOOGLE_MAP_CONTAINER_CLASS =
  'google-map-container flex items-center justify-center';

interface LocationType {
  lat: number;
  lng: number;
}

interface MarkerDataType extends LocationType {
  title: string;
  logo: LogoProps;
}

type CustomGoogleMapPropsType = {
  width: string;
  height: string;
  language: 'ko' | 'en';
  location: LocationType;
  markerData?: MarkerDataType[];
  onMarkerClick?: (id: string) => void;
};

/**
 * - 구글맵을 사용하는 컴포넌트입니다
 * - width, height, language, location, markerData를 props로 받아서 사용합니다
 * - markerData는 배열로 받아서 여러개의 마커를 표시할 수 있습니다
 * @param {string} width - 구글맵의 너비
 * @param {string} height - 구글맵의 높이
 * @param {string} language - 구글맵의 언어
 * @param {object} location - 구글맵의 중심 위치 (위도,경도)
 * @param {array} markerData - 구글맵에 표시할 마커 데이터 (배열)
 * @param {function} onMarkerClick - 마커 클릭시 실행할 함수
 */
export const CustomGoogleMap = memo(
  ({
    width = '100%',
    height = '100%',
    language,
    location,
    markerData,
    onMarkerClick,
  }: CustomGoogleMapPropsType) => {
    let languageOption;

    switch (language) {
      case 'ko':
        languageOption = {
          language: 'ko',
          region: 'KR',
        };
        break;

      case 'en':
        languageOption = {
          language: 'en',
          region: 'US',
        };
        break;
    }

    const isDesktop = useMediaQuery({
      query: `(min-width: ${theme`screens.lg`})`,
    });

    const onLoad = useCallback(function callback(map: any) {
      const bounds = new window.google.maps.LatLngBounds(location);
      map.fitBounds(bounds);
      const listener = google.maps.event.addListener(map, 'idle', function () {
        map.setZoom(18);
        google.maps.event.removeListener(listener);
      });
      // eslint-disable-next-line
    }, []);

    // 마커의 핀 부분을 기준점으로 잡기 위한 함수 (offset 설정 안할 시, 마커의 핀 부분이 아닌 중앙이 기준점이 됨)
    const getPixelPositionOffset = (width: number, height: number) => {
      const triangleHeight = isDesktop ? 16 : 10; // 마커 하단의 삼각형 핀의 높이 (px)
      return {
        x: -(width / 2),
        y: -(height + triangleHeight),
      };
    };

    return (
      <LoadScriptNext
        key={languageOption.language}
        googleMapsApiKey={
          // 비동기로 구글맵을 로딩하며 경고문을 방지하기 위해 loading=async 스트링 추가
          // 관련 옵션이 LoadScriptNext에 없어서 이와 같이 추가함
          // TODO : 아래 NEXT_PUBLIC_GOOGLE_MAPS_API_KEY 값은 매 프로젝트마다 변경 필요 (env 파일 내 링크 참조)
          `${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&loading=async` || ''
        }
        language={languageOption.language}
        region={languageOption.region}
        loadingElement={
          <div
            className={GOOGLE_MAP_CONTAINER_CLASS}
            style={{ width: width || undefined, height: height || undefined }}
          >
            <Spinner variant="apps" />
          </div>
        }
      >
        <div
          className={GOOGLE_MAP_CONTAINER_CLASS}
          style={{ width: width || undefined, height: height || undefined }}
        >
          <GoogleMap
            mapContainerStyle={{
              width: '100%',
              height: '100%',
            }}
            center={location}
            onLoad={onLoad}
            options={{
              zoomControl: false,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              keyboardShortcuts: false,
              styles: darkModeStyle,
            }}
          >
            {markerData &&
              markerData.map((item: any, index: number) => {
                return (
                  <OverlayViewF
                    key={index}
                    position={{
                      lat: item.lat,
                      lng: item.lng,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={getPixelPositionOffset}
                  >
                    <CustomMarker
                      label={item.title}
                      logo={{
                        src: item.logo.src,
                        pcSize: item.logo.pcSize,
                        mobileSize: item.logo.mobileSize,
                      }}
                      onClick={onMarkerClick}
                    />
                  </OverlayViewF>
                );
              })}
          </GoogleMap>
        </div>
      </LoadScriptNext>
    );
  },
);
